import React from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  Paper,
  Chip,
  Stack,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useOptimization } from '../../context/OptimizationContext';
import { OptimizationResults } from '../../types';

interface OptimizationProgressProps {
  compact?: boolean;
}

const OptimizationProgress: React.FC<OptimizationProgressProps> = ({ compact = false }) => {
  const { isRunning, progress, currentAlgorithm, currentIteration, totalIterations, currentRun, totalRuns, completedAlgorithms, results } = useOptimization();

  if (!isRunning && !results) {
    return null;
  }

  if (compact) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {isRunning ? `Running ${currentAlgorithm || 'optimization'}...` : 'Optimization Complete'}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {Math.round(progress)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>
    );
  }

  return (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: '#e8e8e8', // Silver/gray background
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, md: 2 }, flexWrap: 'wrap', gap: 1 }}>
        <TrendingUpIcon sx={{ mr: 1, color: 'primary.main', fontSize: { xs: '1.25rem', md: '1.5rem' } }} />
        <Typography 
          variant="h6" 
          component="h2" 
          sx={{ 
            flexGrow: 1,
            fontSize: { xs: '1rem', md: '1.25rem' }
          }}
        >
          Optimization Progress
        </Typography>
        {!isRunning && (
          <Chip label="Complete" color="success" size="small" />
        )}
      </Box>

      {/* Progress Bar */}
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            fontWeight={500}
            sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
          >
            Overall Optimization Progress
          </Typography>
          <Typography 
            variant="body2" 
            color="primary.main" 
            fontWeight={600}
            sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
          >
            {Math.round(progress)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 16,
            borderRadius: 8,
            backgroundColor: '#c0c0c0', // Silver/gray background for progress bar
            '& .MuiLinearProgress-bar': {
              borderRadius: 8,
              backgroundColor: '#1976d2', // Primary blue for progress
            },
          }}
        />
      </Box>

      {/* Status Information */}
      <Stack spacing={2}>
        {isRunning && (
          <>
            {/* Run Counter - Prominent Display */}
            {totalRuns > 1 && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Run Progress
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700, minWidth: 100 }}>
                    Run {currentRun} / {totalRuns}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(currentRun / totalRuns) * 100}
                    sx={{
                      flexGrow: 1,
                      height: 12,
                      borderRadius: 6,
                      backgroundColor: '#c0c0c0',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 6,
                        backgroundColor: '#0288d1', // Info color for runs
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 45 }}>
                    {Math.round((currentRun / totalRuns) * 100)}%
                  </Typography>
                </Box>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Current Activity
              </Typography>
              {currentAlgorithm && (
                <Box sx={{ mb: 1 }}>
                  <Chip
                    label={`Algorithm: ${currentAlgorithm}`}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 600, mr: 1 }}
                  />
                  {currentIteration > 0 && totalIterations > 0 && (
                    <Chip
                      label={`Iteration: ${currentIteration}/${totalIterations}`}
                      color="secondary"
                      size="small"
                      sx={{ fontWeight: 500, mr: 1 }}
                    />
                  )}
                </Box>
              )}
            </Box>
            {currentIteration > 0 && totalIterations > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Algorithm Iteration Progress
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ minWidth: 80 }}>
                    {currentIteration} / {totalIterations}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(currentIteration / totalIterations) * 100}
                    sx={{
                      flexGrow: 1,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: '#c0c0c0',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 45 }}>
                    {Math.round((currentIteration / totalIterations) * 100)}%
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )}

        <Box>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Algorithms Status
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.5 }}>
            {['GA', 'Cultural', 'SFLA', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'].map((alg) => {
              // Extract algorithm name from currentAlgorithm (handles "Complete Run: X tasks - Algorithm" format)
              const extractAlgorithmName = (algo: string): string => {
                if (algo.includes(' - ')) {
                  return algo.split(' - ').pop() || algo;
                }
                return algo;
              };
              
              const currentAlgName = extractAlgorithmName(currentAlgorithm || '');
              const algIndex = ['GA', 'Cultural', 'SFLA', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'].indexOf(alg);
              const baseProgress = (algIndex / 7) * 100;
              const algProgress = progress / 7;
              
              // For complete run: use completedAlgorithms set
              // For regular optimization: use progress-based calculation
              const isCompleteRun = totalRuns > 1 || (currentAlgorithm && currentAlgorithm.includes('Complete Run'));
              const isCompleted = isCompleteRun 
                ? completedAlgorithms.has(alg)
                : progress > baseProgress + algProgress;
              
              const isCurrent = currentAlgName === alg && isRunning;
              
              // Map algorithm display names to result keys
              const algorithmKeyMap: Record<string, 'ga' | 'culture' | 'frog' | 'sflaga' | 'sflaca' | 'pso' | 'gapso'> = {
                'GA': 'ga',
                'Cultural': 'culture',
                'SFLA': 'frog',
                'SFLAGA': 'sflaga',
                'SFLACA': 'sflaca',
                'PSO': 'pso',
                'GAPSO': 'gapso',
              };
              
              const resultKey = algorithmKeyMap[alg];
              const algorithmResult = results && resultKey ? results[resultKey] : null;
              
              return (
                <Box key={alg} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 'auto' }}>
                  <Chip
                    label={alg}
                    size="small"
                    color={isCompleted ? 'success' : isCurrent ? 'primary' : 'default'}
                    variant={isCompleted ? 'filled' : isCurrent ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: isCurrent ? 600 : 400,
                      opacity: progress < baseProgress ? 0.5 : 1,
                      alignSelf: 'flex-start',
                      px: 0.5,
                    }}
                  />
                  {isCompleted && algorithmResult && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: 0.25,
                      pl: 0.5,
                      fontSize: '0.7rem',
                      color: 'text.secondary',
                    }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                        Cost: {algorithmResult.cost.toFixed(4)}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                        RT: {algorithmResult.responseTime.toFixed(2)} ms
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                        Energy: {algorithmResult.energy.toFixed(4)} J
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
          
        </Box>

        {results && !isRunning && (() => {
          const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;
          const algorithmNames = ['GA', 'Cultural', 'SFLA', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'];
          
          // Filter out undefined results to handle partial results during cancellation
          const validResults = algorithmKeys
            .map((key, index) => ({
              key,
              name: algorithmNames[index],
              result: results[key]
            }))
            .filter(item => item.result !== undefined && item.result !== null);
          
          // Only show summary if we have at least one valid result
          if (validResults.length === 0) {
            return null;
          }
          
          // Find best fitness (highest)
          const fitnessValues = validResults.map(item => item.result!.fitness);
          const maxFitness = Math.max(...fitnessValues);
          const bestFitnessIndex = fitnessValues.indexOf(maxFitness);
          const bestFitnessAlgorithm = validResults[bestFitnessIndex].name;
          
          // Find best energy (lowest)
          const energyValues = validResults.map(item => item.result!.energy);
          const minEnergy = Math.min(...energyValues);
          const bestEnergyIndex = energyValues.indexOf(minEnergy);
          const bestEnergyAlgorithm = validResults[bestEnergyIndex].name;
          
          // Find best cost (lowest)
          const costValues = validResults.map(item => item.result!.cost);
          const minCost = Math.min(...costValues);
          const bestCostIndex = costValues.indexOf(minCost);
          const bestCostAlgorithm = validResults[bestCostIndex].name;
          
          // Find best response time (lowest)
          const responseTimeValues = validResults.map(item => item.result!.responseTime);
          const minResponseTime = Math.min(...responseTimeValues);
          const bestResponseTimeIndex = responseTimeValues.indexOf(minResponseTime);
          const bestResponseTimeAlgorithm = validResults[bestResponseTimeIndex].name;
          
          // Find best execution time (lowest) - if available
          const executionTimeValues = validResults
            .map(item => item.result!.executionTime)
            .filter((v): v is number => Number.isFinite(v));
          const minExecutionTime = executionTimeValues.length > 0 ? Math.min(...executionTimeValues) : Infinity;
          const bestExecutionTimeIndex = executionTimeValues.length > 0 
            ? validResults.findIndex(item => item.result!.executionTime === minExecutionTime)
            : -1;
          const bestExecutionTimeAlgorithm = minExecutionTime !== Infinity && bestExecutionTimeIndex >= 0 
            ? validResults[bestExecutionTimeIndex].name 
            : '';
          
          return (
            <Box>
              <Typography variant="h6" color="text.secondary" display="block" gutterBottom sx={{ fontWeight: 700, mb: 2, fontSize: '1.25rem' }}>
                Best Results Summary
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1.5, alignItems: 'flex-start', overflowX: 'auto' }}>
                {/* Best Fitness */}
                <Box sx={{ flex: '0 0 auto', minWidth: 'fit-content' }}>
                  <Typography variant="body2" color="text.secondary" display="block" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                    Best Fitness Score
                  </Typography>
                  <Typography variant="h6" color="success.main" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    {maxFitness.toFixed(2)} <Typography component="span" variant="body1" color="text.secondary" sx={{ fontWeight: 600, ml: 0.5, fontSize: '0.85rem' }}>({bestFitnessAlgorithm})</Typography>
                  </Typography>
                </Box>
                
                {/* Best Energy */}
                <Box sx={{ flex: '0 0 auto', minWidth: 'fit-content' }}>
                  <Typography variant="body2" color="text.secondary" display="block" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                    Best Energy (Lowest)
                  </Typography>
                  <Typography variant="h6" color="success.main" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    {minEnergy.toFixed(2)} J <Typography component="span" variant="body1" color="text.secondary" sx={{ fontWeight: 600, ml: 0.5, fontSize: '0.85rem' }}>({bestEnergyAlgorithm})</Typography>
                  </Typography>
                </Box>
                
                {/* Best Cost */}
                <Box sx={{ flex: '0 0 auto', minWidth: 'fit-content' }}>
                  <Typography variant="body2" color="text.secondary" display="block" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                    Best Execution Cost (Lowest)
                  </Typography>
                  <Typography variant="h6" color="success.main" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    {minCost.toFixed(2)} <Typography component="span" variant="body1" color="text.secondary" sx={{ fontWeight: 600, ml: 0.5, fontSize: '0.85rem' }}>({bestCostAlgorithm})</Typography>
                  </Typography>
                </Box>
                
                {/* Best Response Time */}
                <Box sx={{ flex: '0 0 auto', minWidth: 'fit-content' }}>
                  <Typography variant="body2" color="text.secondary" display="block" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                    Best Response Time (Lowest)
                  </Typography>
                  <Typography variant="h6" color="success.main" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    {minResponseTime.toFixed(2)} ms <Typography component="span" variant="body1" color="text.secondary" sx={{ fontWeight: 600, ml: 0.5, fontSize: '0.85rem' }}>({bestResponseTimeAlgorithm})</Typography>
                  </Typography>
                </Box>
                
                {/* Best Execution Time */}
                {minExecutionTime !== Infinity && (
                  <Box sx={{ flex: '0 0 auto', minWidth: 'fit-content' }}>
                    <Typography variant="body2" color="text.secondary" display="block" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                      Best Execution Time (Fastest)
                    </Typography>
                    <Typography variant="h6" color="success.main" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      {minExecutionTime.toFixed(2)} ms <Typography component="span" variant="body1" color="text.secondary" sx={{ fontWeight: 600, ml: 0.5, fontSize: '0.85rem' }}>({bestExecutionTimeAlgorithm})</Typography>
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })()}
      </Stack>
    </Paper>
  );
};

export default OptimizationProgress;

